import { useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";

export interface SegmentedOption {
  value: string;
  label: string;
  /** Contador al lado de la etiqueta, en pastilla ("2/3"). Solo lo usa la variante `subtabs`. */
  count?: string;
}

export type SegmentedFilterVariant = "filter" | "view" | "subtabs";

interface Props {
  /** Etiqueta a la izquierda del grupo ("Ordenar", "Garantía"…). El toggle de vista va sin ella. */
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SegmentedOption[];
  /**
   * `filter`: burbuja blanca y texto chico, para las filas de filtros.
   * `view`: burbuja navy con texto blanco y columnas de ancho igual, para el toggle de vista.
   * `subtabs`: track cuadrado y claro con burbuja blanca, para las sub-secciones del expediente.
   */
  variant?: SegmentedFilterVariant;
  /** Prefijo de los `data-testid`: cada opción queda como `<prefix>-<value>`. */
  testIdPrefix: string;
}

/** Grises fríos del track de los filtros: son del diseño y no tienen equivalente en el theme. */
const TRACK_BG = "#DCE6F0";
const TRACK_BORDER = "#CBD9E7";
const LABEL_INACTIVE = "#5C6B7C";

/** Grises del track de los sub-tabs (`.ref-subtabs`), los mismos del resto del expediente. */
const SUBTABS_BG = "#F4F6F9";
const SUBTABS_BORDER = "#D6DBE3";

const VARIANTS = {
  filter: {
    pad: 3,
    fontSize: 12.5,
    padding: "6px 14px",
    bubbleShadow: "0 1px 3px rgba(6, 44, 78, 0.14)",
    radius: "999px",
    trackBg: TRACK_BG,
    trackBorder: TRACK_BORDER,
    /** Reparte el ancho entre las opciones en vez de ajustarse al texto. */
    grow: false,
  },
  view: {
    pad: 4,
    fontSize: 14,
    padding: "9px 26px",
    bubbleShadow: "0 2px 8px rgba(6, 44, 78, 0.28)",
    radius: "999px",
    trackBg: TRACK_BG,
    trackBorder: TRACK_BORDER,
    grow: true,
  },
  subtabs: {
    pad: 4,
    fontSize: 14,
    padding: "10px 14px",
    bubbleShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    radius: "12px",
    trackBg: SUBTABS_BG,
    trackBorder: SUBTABS_BORDER,
    grow: true,
  },
} as const;

/**
 * Grupo segmentado con burbuja deslizante: el indicador se mide del botón activo
 * (`offsetLeft`/`offsetWidth`) y se anima con `transform`, igual que el diseño.
 *
 * Los filtros del marketplace, el toggle tarjetas/comparar y los sub-tabs del expediente son el
 * mismo control con distinto peso visual, de ahí `variant`.
 */
export const SegmentedFilter = ({ label, value, onChange, options, variant = "filter", testIdPrefix }: Props) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [bubble, setBubble] = useState({ left: 0, width: 0 });
  const spec = VARIANTS[variant];
  const activeIndex = Math.max(
    0,
    options.findIndex((opt) => opt.value === value),
  );

  useLayoutEffect(() => {
    const button = trackRef.current?.querySelectorAll("button")[activeIndex];
    if (button) setBubble({ left: button.offsetLeft, width: button.offsetWidth });
  }, [activeIndex, options.length]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {label && <Typography sx={{ fontSize: 12, fontWeight: 600, color: "primary.dark" }}>{label}</Typography>}
      <Box
        ref={trackRef}
        sx={{
          position: "relative",
          display: spec.grow ? "grid" : "inline-flex",
          gridTemplateColumns: spec.grow ? `repeat(${options.length}, 1fr)` : undefined,
          // Los sub-tabs ocupan la fila completa; los otros dos se ajustan a su contenido.
          width: variant === "subtabs" ? "100%" : undefined,
          p: `${spec.pad}px`,
          bgcolor: spec.trackBg,
          border: `1px solid ${spec.trackBorder}`,
          borderRadius: spec.radius,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: `${spec.pad}px`,
            bottom: `${spec.pad}px`,
            left: 0,
            width: bubble.width,
            borderRadius: variant === "subtabs" ? "8px" : "999px",
            bgcolor: variant === "view" ? "primary.dark" : "background.paper",
            boxShadow: spec.bubbleShadow,
            transform: `translateX(${bubble.left}px)`,
            transition: "transform .32s cubic-bezier(.34,1.4,.5,1), width .32s cubic-bezier(.34,1.4,.5,1)",
          }}
        />
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <ButtonBase
              key={opt.value}
              onClick={() => onChange(opt.value)}
              data-testid={`${testIdPrefix}-${opt.value || "default"}`}
              sx={{
                position: "relative",
                zIndex: 1,
                whiteSpace: "nowrap",
                gap: "10px",
                fontWeight: 600,
                fontSize: spec.fontSize,
                p: spec.padding,
                borderRadius: variant === "subtabs" ? "8px" : "999px",
                color: selected ? (variant === "view" ? "#fff" : "primary.dark") : LABEL_INACTIVE,
                transition: "color .2s",
              }}
            >
              {opt.label}
              {opt.count && (
                <Typography
                  component="span"
                  variant="body3"
                  sx={(theme) => ({
                    p: "2px 8px",
                    borderRadius: "999px",
                    border: "1px solid",
                    fontVariantNumeric: "tabular-nums",
                    ...(selected
                      ? {
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: "primary.main",
                        }
                      : { bgcolor: SUBTABS_BG, borderColor: SUBTABS_BORDER, color: "#7A8497" }),
                  })}
                >
                  {opt.count}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};
